import {
  BookingCalendarSteps,
  EventTimeZoneTypeEnum,
} from 'common/components/entities/Calendar/constants'
import { useEvent } from 'common/components/entities/Calendar/hooks/use-event'
import { getStartEndDates } from 'common/components/entities/Calendar/utils'
import { BookingCalendarInterface } from 'common/types/entities/BookingCalendarInterface'
import { getLocalTimeZone } from 'common/utils/date-utils'
import { getEvent, getPreviewEvent } from 'publisher/api/booking-calendar-api'
import { useEventTimeSlots } from 'publisher/hooks/use-event-time-slots'
import { useEffect, useState } from 'react'
import { useCalculateTypography } from 'src/publisher/shared/hooks/use-calculate-typography'
import { useCalendarSelectors } from './use-calendar-selectors'

export const useBookingCalendarParams = ({
  entity,
  isPreview,
}: {
  entity: BookingCalendarInterface
  isPreview?: boolean
}) => {
  const [activeStep, setActiveStep] = useState(
    BookingCalendarSteps.CalendarStep,
  )

  const {
    selectedDate,
    selectedTimeSlot,
    selectedLocationId,
    selectedTimeZone,
    handleChangeField,
  } = useCalendarSelectors()

  const { event, isFetching: isEventFetching } = useEvent({
    eventId: entity.eventId,
    fetcher: isPreview ? getPreviewEvent : getEvent,
  })
  const { timeSlots = [], isFetching: isTimeSlotsFetching } = useEventTimeSlots(
    { isPreview },
  )

  useEffect(() => {
    if (event) {
      handleChangeField('eventId', event.id!)
      handleChangeField('ownerId', event.ownerId)
      handleChangeField('eventLocationId', event.locations?.[0]?.id)
      handleChangeField(
        'timeZone',
        event.displayTimezoneType === EventTimeZoneTypeEnum.Local
          ? getLocalTimeZone()
          : event.displayTimezone || getLocalTimeZone(),
      )
    }
  }, [event])

  const onMonthSelect = (month: Date) => {
    const { startDate, endDate } = getStartEndDates(month)
    handleChangeField('startDate', startDate)
    handleChangeField('endDate', endDate)
    handleChangeField('date', '')
    handleChangeField('timeSlot', '')
  }

  const { calculatedFontSize, calculatedFontFamily } = useCalculateTypography()

  return {
    activeStep,
    setActiveStep,
    selectedDate,
    selectedTimeSlot,
    selectedTimeZone,
    selectedLocationId,
    handleChangeField,
    event,
    isEventFetching,
    timeSlots,
    isTimeSlotsFetching,
    onMonthSelect,
    globalFontSize: calculatedFontSize.desktop,
    globalMobileFontSize: calculatedFontSize.mobile,
    globalFontFamily: calculatedFontFamily.desktop,
  }
}
